import { ReactNode, useMemo } from 'react';
import { TranslateInterface } from 'utils/translation';
import ClassNames from '@streamloots/classnames';
import { Badge, BadgeColors, BadgeColorsType } from 'ui-library/badge';
import AvatarImage from 'components/avatar-image';
import { PageFeedComment } from 'model/indexTS';
import { currencyManager } from 'services/culture';
import { Currency } from '@streamloots/streamloots-types';
import { DateUtils } from 'domain/date-utils';
import theme from './feed-comments.scss';

const classNames = ClassNames(theme);

export interface FeedCommentViewProps extends TranslateInterface {
  feedComment: PageFeedComment;
  isPreview?: boolean;
  children?: ReactNode;
  color?: BadgeColorsType;
}

interface FooterPreviewProps {
  feedComment: PageFeedComment;
  color?: BadgeColorsType;
}

const FooterPreview = ({ feedComment, color }: FooterPreviewProps) => {
  const formattedAmount = currencyManager.getFormattedPrice(
    feedComment.tippedAmount,
    feedComment.tippedCurrency ?? Currency.EUR,
  );
  return (
    <div className={classNames('feed-comment-footer')}>
      <div className={classNames('feed-comment-footer__section')} />
      <div className={classNames('feed-comment-footer__section')}>
        <Badge label={formattedAmount} color={color} />
      </div>
    </div>
  );
};

export const FeedCommentView = ({
  feedComment,
  children,
  isPreview = false,
  t,
  color,
}: FeedCommentViewProps): JSX.Element => {
  const badgeColor = useMemo(() => {
    if (!feedComment.hasTipped) {
      return undefined;
    }

    if (feedComment.tippedAmount >= 100) {
      return BadgeColors.Dangerous;
    }

    if (feedComment.tippedAmount >= 50) {
      return BadgeColors.Success;
    }

    if (feedComment.tippedAmount >= 10) {
      return BadgeColors.Warning;
    }

    return BadgeColors.Epic;
  }, [feedComment.hasTipped, feedComment.tippedAmount]);

  const hasColor = color || badgeColor;

  return (
    <div
      className={classNames({
        'feed-comments': true,
        ...(hasColor && { [`feed-comments--support-${hasColor}`]: true }),
        'feed-comments--preview': isPreview,
        'feed-comments--support': feedComment.hasTipped,
      })}
    >
      <div className={classNames('feed-comments__detail')}>
        <AvatarImage className={classNames('feed-comments__detail__avatar')} src={feedComment.avatarImageUrl} />
        <div>
          <span className={classNames('feed-comments__detail__title')}>{feedComment.username}</span>
          {feedComment.hasTipped && (
            <Badge
              className={classNames('feed-comments__detail__support')}
              label={t('pageFeed:feedDetail.comments.supporter')}
              color="info"
            />
          )}
          <span className={classNames('feed-comments__detail__time')}>{DateUtils.fromNow(feedComment.createdAt)}</span>
          <span className={classNames('feed-comments__detail__title')}>-</span>
          <span
            className={classNames({
              'feed-comments__detail__content': true,
              'feed-comments__detail__content--deleted': feedComment.deleted,
            })}
          >
            {feedComment.deleted ? t('pageFeed:feedDetail.comments.deleted') : feedComment.content}
          </span>
        </div>
      </div>
      {isPreview ? <FooterPreview color={color || badgeColor} feedComment={feedComment} /> : children}
    </div>
  );
};
